import type { Point } from "../canvas/canvasTypes";
import type {
  IntegerRect,
  RasterSelectionHandle,
  RasterSelectionRotationHandleSide,
  RasterSelectionTransform,
} from "./selectionTypes";
import { MAX_SELECTION_PIXELS } from "./selectionMemory";

export const MIN_SELECTION_DIMENSION = 4;
export const SELECTION_HANDLE_RADIUS_CSS_PIXELS = 22;
export const SELECTION_ROTATION_HANDLE_OFFSET_CSS_PIXELS = 54;

const EPSILON = 0.0001;

function finite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

export function integerConstraintRect(rect: Readonly<IntegerRect>): IntegerRect {
  const left = Math.ceil(finite(rect.x));
  const top = Math.ceil(finite(rect.y));
  const right = Math.floor(finite(rect.x) + Math.max(0, finite(rect.width)));
  const bottom = Math.floor(finite(rect.y) + Math.max(0, finite(rect.height)));
  return {
    x: left,
    y: top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

export function normalizeSelectionRect(
  start: Readonly<Point>,
  end: Readonly<Point>,
  constraint: Readonly<IntegerRect>,
): IntegerRect | null {
  const safeConstraint = integerConstraintRect(constraint);
  const constraintRight = safeConstraint.x + safeConstraint.width;
  const constraintBottom = safeConstraint.y + safeConstraint.height;
  const left = Math.max(safeConstraint.x, Math.floor(Math.min(start.x, end.x)));
  const top = Math.max(safeConstraint.y, Math.floor(Math.min(start.y, end.y)));
  const right = Math.min(constraintRight, Math.ceil(Math.max(start.x, end.x)));
  const bottom = Math.min(constraintBottom, Math.ceil(Math.max(start.y, end.y)));
  const rect = {
    x: left,
    y: top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
  return isUsableSelectionRect(rect) ? rect : null;
}

export function isUsableSelectionRect(rect: Readonly<IntegerRect>): boolean {
  return (
    Number.isSafeInteger(rect.x) &&
    Number.isSafeInteger(rect.y) &&
    Number.isSafeInteger(rect.width) &&
    Number.isSafeInteger(rect.height) &&
    rect.width >= MIN_SELECTION_DIMENSION &&
    rect.height >= MIN_SELECTION_DIMENSION
  );
}

export function rectToSelectionTransform(rect: Readonly<IntegerRect>): RasterSelectionTransform {
  return {
    centre: { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
    width: rect.width,
    height: rect.height,
    rotation: 0,
    flipX: false,
    flipY: false,
  };
}

function rotateVector(point: Readonly<Point>, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return {
    x: point.x * cosine - point.y * sine,
    y: point.x * sine + point.y * cosine,
  };
}

export function selectionTransformCorners(
  transform: Readonly<RasterSelectionTransform>,
): Record<Exclude<RasterSelectionHandle, "body" | "rotate">, Point> {
  const halfWidth = transform.width / 2;
  const halfHeight = transform.height / 2;
  const place = (x: number, y: number): Point => {
    const rotated = rotateVector({ x, y }, transform.rotation);
    return { x: transform.centre.x + rotated.x, y: transform.centre.y + rotated.y };
  };
  return {
    "resize-north-west": place(-halfWidth, -halfHeight),
    "resize-north-east": place(halfWidth, -halfHeight),
    "resize-south-east": place(halfWidth, halfHeight),
    "resize-south-west": place(-halfWidth, halfHeight),
  };
}

export function selectionRotationHandle(
  transform: Readonly<RasterSelectionTransform>,
  zoom: number,
  constraint?: Readonly<IntegerRect>,
  lockedSide?: RasterSelectionRotationHandleSide | null,
): Point {
  return selectionRotationHandlePlacement(transform, zoom, constraint, lockedSide).point;
}

export interface RasterSelectionRotationHandlePlacement {
  readonly point: Point;
  readonly anchor: Point;
  readonly side: RasterSelectionRotationHandleSide;
}

function squaredDistanceOutsideRect(point: Readonly<Point>, rect: Readonly<IntegerRect>): number {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const dx = Math.max(rect.x - point.x, 0, point.x - right);
  const dy = Math.max(rect.y - point.y, 0, point.y - bottom);
  return dx * dx + dy * dy;
}

export function selectionRotationHandlePlacement(
  transform: Readonly<RasterSelectionTransform>,
  zoom: number,
  constraint?: Readonly<IntegerRect>,
  lockedSide?: RasterSelectionRotationHandleSide | null,
): RasterSelectionRotationHandlePlacement {
  const safeZoom = Math.max(0.05, finite(zoom, 1));
  const offset = SELECTION_ROTATION_HANDLE_OFFSET_CSS_PIXELS / safeZoom;
  const place = (
    side: RasterSelectionRotationHandleSide,
  ): RasterSelectionRotationHandlePlacement => {
    const direction = side === "north" ? -1 : 1;
    const anchorOffset = rotateVector(
      { x: 0, y: (direction * transform.height) / 2 },
      transform.rotation,
    );
    const handleOffset = rotateVector(
      { x: 0, y: direction * (transform.height / 2 + offset) },
      transform.rotation,
    );
    return {
      anchor: {
        x: transform.centre.x + anchorOffset.x,
        y: transform.centre.y + anchorOffset.y,
      },
      point: {
        x: transform.centre.x + handleOffset.x,
        y: transform.centre.y + handleOffset.y,
      },
      side,
    };
  };
  if (lockedSide) return place(lockedSide);
  const north = place("north");
  if (!constraint) return north;

  const safeConstraint = integerConstraintRect(constraint);
  const radius = SELECTION_HANDLE_RADIUS_CSS_PIXELS / safeZoom;
  const touchTargetConstraint = {
    x: safeConstraint.x + Math.min(radius, safeConstraint.width / 2),
    y: safeConstraint.y + Math.min(radius, safeConstraint.height / 2),
    width: Math.max(0, safeConstraint.width - Math.min(radius * 2, safeConstraint.width)),
    height: Math.max(0, safeConstraint.height - Math.min(radius * 2, safeConstraint.height)),
  };
  const south = place("south");
  const northDistance = squaredDistanceOutsideRect(north.point, touchTargetConstraint);
  const southDistance = squaredDistanceOutsideRect(south.point, touchTargetConstraint);
  // Prefer the traditional north side when both choices are effectively tied
  // (for example, a selection spanning a complete comic panel). A one-device-
  // pixel tolerance avoids floating-point noise switching the handle into the
  // opposite gutter after viewport scaling.
  return Math.sqrt(southDistance) + 1 / safeZoom < Math.sqrt(northDistance) ? south : north;
}

export function selectionTransformBounds(transform: Readonly<RasterSelectionTransform>): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const radians = (finite(transform.rotation) * Math.PI) / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  const width = cosine * transform.width + sine * transform.height;
  const height = sine * transform.width + cosine * transform.height;
  return {
    x: transform.centre.x - width / 2,
    y: transform.centre.y - height / 2,
    width,
    height,
  };
}

export function selectionTransformToIntegerRect(
  transform: Readonly<RasterSelectionTransform>,
  constraint: Readonly<IntegerRect>,
): IntegerRect {
  const bounds = selectionTransformBounds(transform);
  const safeConstraint = integerConstraintRect(constraint);
  const left = Math.max(safeConstraint.x, Math.floor(bounds.x + EPSILON));
  const top = Math.max(safeConstraint.y, Math.floor(bounds.y + EPSILON));
  const right = Math.min(
    safeConstraint.x + safeConstraint.width,
    Math.ceil(bounds.x + bounds.width - EPSILON),
  );
  const bottom = Math.min(
    safeConstraint.y + safeConstraint.height,
    Math.ceil(bounds.y + bounds.height - EPSILON),
  );
  return {
    x: left,
    y: top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

export function clampSelectionTransform(
  transform: Readonly<RasterSelectionTransform>,
  constraint: Readonly<IntegerRect>,
): RasterSelectionTransform {
  const safeConstraint = integerConstraintRect(constraint);
  let width = Math.max(MIN_SELECTION_DIMENSION, Math.abs(finite(transform.width, 1)));
  let height = Math.max(MIN_SELECTION_DIMENSION, Math.abs(finite(transform.height, 1)));
  const rotation = finite(transform.rotation);
  const radians = (rotation * Math.PI) / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  const rotatedWidth = cosine * width + sine * height;
  const rotatedHeight = sine * width + cosine * height;
  const scale = Math.min(
    1,
    safeConstraint.width > 0 ? safeConstraint.width / Math.max(EPSILON, rotatedWidth) : 0,
    safeConstraint.height > 0 ? safeConstraint.height / Math.max(EPSILON, rotatedHeight) : 0,
  );
  if (scale < 1) {
    width = Math.max(1, width * Math.max(0, scale));
    height = Math.max(1, height * Math.max(0, scale));
  }
  let scaledRotatedWidth = cosine * width + sine * height;
  let scaledRotatedHeight = sine * width + cosine * height;
  const transformedPixels = scaledRotatedWidth * scaledRotatedHeight;
  if (transformedPixels > MAX_SELECTION_PIXELS) {
    const memoryScale = Math.sqrt(MAX_SELECTION_PIXELS / transformedPixels);
    width = Math.max(1, width * memoryScale);
    height = Math.max(1, height * memoryScale);
    scaledRotatedWidth = cosine * width + sine * height;
    scaledRotatedHeight = sine * width + cosine * height;
  }
  const minCentreX = safeConstraint.x + scaledRotatedWidth / 2;
  const maxCentreX = safeConstraint.x + safeConstraint.width - scaledRotatedWidth / 2;
  const minCentreY = safeConstraint.y + scaledRotatedHeight / 2;
  const maxCentreY = safeConstraint.y + safeConstraint.height - scaledRotatedHeight / 2;
  const centreX = Math.min(
    Math.max(finite(transform.centre.x, minCentreX), minCentreX),
    Math.max(minCentreX, maxCentreX),
  );
  const centreY = Math.min(
    Math.max(finite(transform.centre.y, minCentreY), minCentreY),
    Math.max(minCentreY, maxCentreY),
  );
  return {
    centre: { x: centreX, y: centreY },
    width,
    height,
    rotation,
    flipX: Boolean(transform.flipX),
    flipY: Boolean(transform.flipY),
  };
}

export function moveSelectionTransform(
  original: Readonly<RasterSelectionTransform>,
  delta: Readonly<Point>,
  constraint: Readonly<IntegerRect>,
): RasterSelectionTransform {
  return clampSelectionTransform(
    {
      ...original,
      centre: {
        x: original.centre.x + finite(delta.x),
        y: original.centre.y + finite(delta.y),
      },
    },
    constraint,
  );
}

const OPPOSITE_HANDLE: Record<
  Exclude<RasterSelectionHandle, "body" | "rotate">,
  Exclude<RasterSelectionHandle, "body" | "rotate">
> = {
  "resize-north-west": "resize-south-east",
  "resize-north-east": "resize-south-west",
  "resize-south-east": "resize-north-west",
  "resize-south-west": "resize-north-east",
};

export function resizeSelectionTransform(
  original: Readonly<RasterSelectionTransform>,
  handle: Exclude<RasterSelectionHandle, "body" | "rotate">,
  handlePoint: Readonly<Point>,
  constraint: Readonly<IntegerRect>,
): RasterSelectionTransform {
  const corners = selectionTransformCorners(original);
  const opposite = corners[OPPOSITE_HANDLE[handle]];
  const localDelta = rotateVector(
    { x: handlePoint.x - opposite.x, y: handlePoint.y - opposite.y },
    -original.rotation,
  );
  const localCentre = { x: localDelta.x / 2, y: localDelta.y / 2 };
  const centreOffset = rotateVector(localCentre, original.rotation);
  return clampSelectionTransform(
    {
      ...original,
      centre: { x: opposite.x + centreOffset.x, y: opposite.y + centreOffset.y },
      width: Math.max(MIN_SELECTION_DIMENSION, Math.abs(localDelta.x)),
      height: Math.max(MIN_SELECTION_DIMENSION, Math.abs(localDelta.y)),
    },
    constraint,
  );
}

export function selectionPointerAngleDegrees(
  centre: Readonly<Point>,
  pointer: Readonly<Point>,
): number {
  return (Math.atan2(pointer.y - centre.y, pointer.x - centre.x) * 180) / Math.PI;
}

export function shortestSignedAngularDeltaDegrees(
  startAngle: number,
  currentAngle: number,
): number {
  const start = finite(startAngle);
  const current = finite(currentAngle);
  return ((current - start + 540) % 360) - 180;
}

export function rotateSelectionTransform(
  original: Readonly<RasterSelectionTransform>,
  angularDelta: number,
  constraint: Readonly<IntegerRect>,
): RasterSelectionTransform {
  return clampSelectionTransform(
    { ...original, rotation: finite(original.rotation) + finite(angularDelta) },
    constraint,
  );
}

export function flipSelectionTransform(
  original: Readonly<RasterSelectionTransform>,
  axis: "horizontal" | "vertical",
): RasterSelectionTransform {
  return {
    ...original,
    centre: { ...original.centre },
    flipX: axis === "horizontal" ? !original.flipX : original.flipX,
    flipY: axis === "vertical" ? !original.flipY : original.flipY,
  };
}

function pointInTransform(
  point: Readonly<Point>,
  transform: Readonly<RasterSelectionTransform>,
): boolean {
  const local = rotateVector(
    { x: point.x - transform.centre.x, y: point.y - transform.centre.y },
    -transform.rotation,
  );
  return Math.abs(local.x) <= transform.width / 2 && Math.abs(local.y) <= transform.height / 2;
}

export function hitTestSelectionTransform(
  point: Readonly<Point>,
  transform: Readonly<RasterSelectionTransform>,
  zoom: number,
  constraint?: Readonly<IntegerRect>,
  lockedRotationHandleSide?: RasterSelectionRotationHandleSide | null,
): RasterSelectionHandle | null {
  const safeZoom = Math.max(0.05, finite(zoom, 1));
  const radius = SELECTION_HANDLE_RADIUS_CSS_PIXELS / safeZoom;
  const rotationHandle = selectionRotationHandle(
    transform,
    safeZoom,
    constraint,
    lockedRotationHandleSide,
  );
  if (Math.hypot(point.x - rotationHandle.x, point.y - rotationHandle.y) <= radius) {
    return "rotate";
  }
  const corners = selectionTransformCorners(transform);
  for (const [handle, corner] of Object.entries(corners) as Array<
    [Exclude<RasterSelectionHandle, "body" | "rotate">, Point]
  >) {
    if (Math.hypot(point.x - corner.x, point.y - corner.y) <= radius) return handle;
  }
  return pointInTransform(point, transform) ? "body" : null;
}

export function transformsDiffer(
  left: Readonly<RasterSelectionTransform>,
  right: Readonly<RasterSelectionTransform>,
): boolean {
  return (
    Math.abs(left.centre.x - right.centre.x) > EPSILON ||
    Math.abs(left.centre.y - right.centre.y) > EPSILON ||
    Math.abs(left.width - right.width) > EPSILON ||
    Math.abs(left.height - right.height) > EPSILON ||
    Math.abs(left.rotation - right.rotation) > EPSILON ||
    left.flipX !== right.flipX ||
    left.flipY !== right.flipY
  );
}
